import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SearchIcon from '@mui/icons-material/Search';
import axiosInstance from '../../../services/axiosInterceptors';
import apiEndPointsConfig from '../../../services/apiEndPoints';
import nipponBotApiEndpoints from '../../../services/nipponBotApiEndpoints';
import { theme as colorTheme } from '../../../Convoze.theme';

const primary = colorTheme('palette', 'primary').value;

const todayDate = () => new Date().toISOString().split('T')[0];

const buildMisPayload = ({ startDate, endDate, searchTerm }) => ({
  startDate: startDate || todayDate(),
  endDate: endDate || todayDate(),
  searchTerm: searchTerm || '',
  nipponBotExport: true
});

export default function NipponBotMisReport({ onMessage }) {
  const token = localStorage.getItem('token');
  const [startDate, setStartDate] = useState(todayDate());
  const [endDate, setEndDate] = useState(todayDate());
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  const payload = useMemo(
    () => buildMisPayload({ startDate, endDate, searchTerm: appliedSearch }),
    [startDate, endDate, appliedSearch]
  );

  const fetchMisReport = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axiosInstance.post(nipponBotApiEndpoints.interactionsMisReport, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response?.data?.data || {};
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setColumns(Array.isArray(data.columns) ? data.columns : []);
      setTotalCount(Number(data.totalCount || 0));
    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data?.status?.value || 'Failed to load MIS report.';
      setError(message);
      onMessage?.(message, 'error');
      setRows([]);
      setColumns([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMisReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload]);

  const handleSearch = (event) => {
    event.preventDefault();
    setAppliedSearch(searchTerm.trim());
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axiosInstance.post(apiEndPointsConfig.exportExcelInteraction, payload, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      if (!response.data) return;

      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      );
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `nippon_bot_mis_report_${todayDate()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data?.status?.value || 'Failed to export MIS report.';
      onMessage?.(message, 'error');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Paper sx={{ boxShadow: 0, border: 1, borderColor: 'grey.200', borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ p: 3, borderBottom: '1px solid #EEEEEE' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>MIS Report</Typography>
            <Typography variant="body2" color="text.secondary">
              Showing the same report-ready call data used in the Excel export.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={exporting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <FileDownloadIcon />}
            onClick={handleExport}
            disabled={exporting || loading || !rows.length}
            sx={{
              textTransform: 'none',
              minWidth: 150,
              backgroundColor: primary,
              borderRadius: '12px',
              '&:hover': { backgroundColor: '#5f2ab8' },
            }}
          >
            {exporting ? 'Exporting...' : 'Export Excel'}
          </Button>
        </Stack>

        <Box component="form" onSubmit={handleSearch} sx={{ mt: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
            <TextField
              size="small"
              type="date"
              label="Start Date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              type="date"
              label="End Date"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              label="Search MIS Report"
              placeholder="Search candidate or file..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              sx={{ width: { xs: '100%', md: 320 } }}
            />
            <Button
              type="submit"
              variant="outlined"
              startIcon={<SearchIcon />}
              sx={{ textTransform: 'none', color: primary, borderColor: primary, '&:hover': { borderColor: primary, backgroundColor: `${primary}14` } }}
            >
              Search
            </Button>
          </Stack>
        </Box>
      </Box>

      {error ? <Alert severity="error" sx={{ m: 2 }}>{error}</Alert> : null}

      <TableContainer sx={{ maxHeight: 'calc(100vh - 315px)', overflow: 'auto' }}>
        <Table stickyHeader size="small" sx={{ minWidth: 2600 }}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column}
                  sx={{
                    fontWeight: 550,
                    textTransform: 'uppercase',
                    fontSize: '0.7rem',
                    color: '#6b7280',
                    backgroundColor: '#f9fafb',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {column}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={Math.max(columns.length, 1)} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : rows.length ? (
              rows.map((row, rowIndex) => (
                <TableRow key={`mis-row-${rowIndex}`} hover>
                  {columns.map((column) => (
                    <TableCell key={`${rowIndex}-${column}`} sx={{ whiteSpace: 'nowrap', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {row?.[column] ?? '-'}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={Math.max(columns.length, 1)} align="center" sx={{ py: 6 }}>
                  <Typography color="text.secondary">No MIS report-ready calls found for the selected filters.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ px: 3, py: 1.5, borderTop: '1px solid #eef0f4' }}>
        <Typography variant="body2" color="text.secondary">
          Showing {totalCount} result{totalCount === 1 ? '' : 's'}
        </Typography>
      </Box>
    </Paper>
  );
}
